import moment from "moment";
import { groupBy } from "ramda";
import { type FC, type ReactElement, memo, useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { CSSTransition, TransitionGroup } from "react-transition-group";
import { Waypoint as ReactWaypoint } from "react-waypoint";

import { ActionButtonIntent, LoadingSpinner } from "widgets";
import { useURLSearchParam } from "widgets/hooks";

import { type Dictionary } from "../../app-utils/type.js";
import { type Project, type ProjectActivity as ProjectActivityGql, type User } from "../../graphql.js";
import { deleteActivities } from "../../redux/modules/project.js";
import { type IAppState } from "../../redux/reducer.js";
import { type ReduxDispatch } from "../../redux/redux-utils.js";
import { ContentPlaceholder } from "../core/ContentPlaceholder.js";
import { ConfirmationDialog } from "../core/overlays/Dialogs.js";
import { ModalControlledOverlay } from "../core/overlays/index.js";
import { ProjectActivity } from "./ProjectActivity.js";
import { ProjectActivitySearchEngine } from "./ProjectActivitySearchEngine.js";

export const PROJECT_ACTIVITY_DAY_FORMAT = "MMMM Do YYYY";
export const PROJECT_ACTIVITY_WEEK_FORMAT = "MMMM Do";

export interface IProjectActivityStreamProps {
  activities: ProjectActivityGql[];
  filterExpression: string;
  filterTermList: string[];

  scrollTargetToId: boolean;

  enableScrollToBottom: () => void;
  disableScrollToBottom: () => void;
}

const NO_MATCH_FOUND_MSG = "No items meet the search criteria";
const LATEST_ACTIVITY_GROUP_KEY = "Today";

export const ProjectActivityStream: FC<IProjectActivityStreamProps> = memo(function ProjectActivityStream(props) {
  const {
    activities,
    filterExpression,
    filterTermList,
    scrollTargetToId,
    enableScrollToBottom,
    disableScrollToBottom,
  } = props;

  const currentUser = useSelector<IAppState, User>((state) => state.auth.session);
  const project = useSelector<IAppState, Project>((state) => state.project.project);
  const dispatch = useDispatch<ReduxDispatch>();

  const [groupedActivities, setGroupedActivities] = useState<Dictionary<ProjectActivityGql[]>>({});
  const [activityToBeDeleted, setActivityToBeDeleted] = useState<ProjectActivityGql>(null);

  useEffect(() => {
    let filteredActivities: ProjectActivityGql[] = [];
    if (filterTermList?.length && activities) {
      const projectMembers = project.projectMembers.map((x) => x.user);
      filteredActivities = activities.filter((activity) => {
        // isSelfActivity will allow to filter against "You" keyword
        const isSelfActivity: boolean = activity.user.username === currentUser?.username;
        return ProjectActivitySearchEngine.matchFilter(activity, filterTermList, projectMembers, isSelfActivity);
      });
    } else {
      filteredActivities = activities?.slice() || []; // slice allow to workaround the reverse() effect
    }

    if (filteredActivities?.length) {
      // there are some activities to show, we need to group them
      const grouped = groupBy((activity: ProjectActivityGql) => {
        return moment(activity.created).calendar(null, {
          sameDay: "[" + LATEST_ACTIVITY_GROUP_KEY + "]",
          lastDay: "[Yesterday]",
          lastWeek: PROJECT_ACTIVITY_WEEK_FORMAT,
          sameElse: PROJECT_ACTIVITY_DAY_FORMAT,
        });
      }, filteredActivities);
      // make sure the Today section is always there even if no activity is available ;
      // that will ensure the transition is working when a new activity pops up ;
      // still the empty TODAY section needs to be hidden if empty
      if (!Object.prototype.hasOwnProperty.call(grouped, LATEST_ACTIVITY_GROUP_KEY)) {
        grouped[LATEST_ACTIVITY_GROUP_KEY] = [];
      }

      setGroupedActivities(grouped);
    } else {
      setGroupedActivities({});
    }
  }, [activities, filterTermList]);

  // convert the json object created by the Moment method above to an array of activity groups
  //   reverse the final list to have latest at the bottom.
  const moments: string[] = Object.keys(groupedActivities);

  if (!moments.length) {
    if (filterTermList?.length) {
      return <ContentPlaceholder key="noMatchFound">{NO_MATCH_FOUND_MSG}</ContentPlaceholder>;
    }
    return <LoadingSpinner inCenter={true} />;
  }

  const mostRecentMoment = moments[0];
  return (
    <>
      {moments.reverse().map((moment) => (
        <GroupedProjectActivity
          key={moment}
          activities={groupedActivities[moment]}
          mostRecentMoment={mostRecentMoment}
          groupHeader={moment}
          filterExpression={filterExpression}
          scrollTargetToId={scrollTargetToId}
          enableScrollToBottom={enableScrollToBottom}
          disableScrollToBottom={disableScrollToBottom}
          onDeleteActivityClicked={setActivityToBeDeleted}
        />
      ))}

      {activityToBeDeleted && (
        <ModalControlledOverlay close={() => setActivityToBeDeleted(null)} isOpen={true}>
          {({ close }) => (
            <ConfirmationDialog
              key={"delete-activity-confirmation-dialog"}
              headerProps={{
                title: `Delete ${activityToBeDeleted.type}`,
              }}
              mainActionButtonProps={{
                children: "Delete",
                onPress: () =>
                  dispatch(deleteActivities([activityToBeDeleted.id], activityToBeDeleted.type)).then(() => close()),
                intent: ActionButtonIntent.Danger,
              }}
              closeFn={close}
            >
              Are you sure you want to delete this {activityToBeDeleted.type}?
            </ConfirmationDialog>
          )}
        </ModalControlledOverlay>
      )}
    </>
  );
});

export interface IGroupedProjectActivityProps {
  activities: ProjectActivityGql[];
  filterExpression: string; // simply to deactivate transitions during filtering
  groupHeader: string;
  mostRecentMoment: string;
  scrollTargetToId: boolean;

  enableScrollToBottom: () => void;
  disableScrollToBottom: () => void;
  onDeleteActivityClicked: (ProjectActivityGql: ProjectActivityGql) => void;
}

const GroupedProjectActivity: FC<IGroupedProjectActivityProps> = memo(function GroupedProjectActivity(props) {
  const { activities, groupHeader, mostRecentMoment, filterExpression, onDeleteActivityClicked, scrollTargetToId } =
    props;

  const currentUser = useSelector<IAppState, User>((state) => state.auth.session);
  const project = useSelector<IAppState, Project>((state) => state.project.project);
  const scrollActivityId = useURLSearchParam("activityid");

  const buildActivity = useCallback(
    (
      activity: ProjectActivityGql,
      index: number,
      groupedActivities: ProjectActivityGql[],
      groupHeader: string,
      mostRecentMoment: string,
    ): { activityKey: string; item: ReactElement } => {
      // The key value of the project activity is set to a temporary value if it is the scroll target
      // and scrolling did not happen yet. After scrolling is finished, the key value is replaced by the usual value.
      // This transition of the key value triggers the visual effect.
      // Note that there may only be one scroll target on a page, therefore a constant key is fine.

      // we want to highlight the activity corresponding to activityid query param only the first time or when the value changes : when scroll event has terminated the
      // scroll target is set to noTarget. Controls the highlighting of the activity. This is why we provide the "activityKey" to the parent CSSTransition component
      const isScrollTargetActivity: boolean = scrollActivityId == activity.id;
      const activityKey: string = scrollTargetToId && isScrollTargetActivity ? "pa-scroll" : "pa-" + activity.id;

      const ProjectActivityCmpt: ReactElement = (
        <ProjectActivity
          key={activity.id}
          activity={activity}
          className={
            index === groupedActivities.length - 1 && groupHeader !== mostRecentMoment ? "no-bottom-margin" : undefined
          }
          currentUser={currentUser}
          onDeleteClicked={onDeleteActivityClicked}
          users={project?.projectMembers?.map((x) => x.user)}
        />
      );
      // the latest activity is wrapped into a scroll spy to make sure it is visible :
      // this will drive the decision of automatic scroll to bottom when a new activity enters
      const latestActivity: boolean =
        groupHeader === LATEST_ACTIVITY_GROUP_KEY && index === groupedActivities.length - 1;
      return {
        activityKey: activityKey,
        item: latestActivity ? (
          <ReactWaypoint
            bottomOffset="-300px"
            key={activityKey}
            onEnter={props.enableScrollToBottom}
            onLeave={props.disableScrollToBottom}
          >
            <span>{ProjectActivityCmpt}</span>
          </ReactWaypoint>
        ) : (
          <span key={activityKey}>{ProjectActivityCmpt}</span>
        ),
      };
    },
    [scrollActivityId, scrollTargetToId],
  );

  const reportActivities: Array<{ activityKey: string; item: ReactElement }> = useMemo(() => {
    return activities
      .slice()
      .reverse()
      .map((activity: ProjectActivityGql, index: number) =>
        buildActivity(activity, index, activities, groupHeader, mostRecentMoment),
      );
  }, [activities, buildActivity]);

  return (
    <div key={groupHeader} style={{ display: reportActivities.length === 0 ? "none" : "inherit" }}>
      <div className="conversation-group-header">
        <span key="conversation-group-header-1">{groupHeader}</span>
      </div>

      <ul className="conversation-list">
        {
          // it is important to deactivate the transitions during filtering otherwise flashing will happen
          filterExpression?.length ? (
            <span>{reportActivities.map((reportActivity) => reportActivity.item)}</span>
          ) : (
            <TransitionGroup>
              {reportActivities.map((reportActivity) => (
                <CSSTransition key={reportActivity.activityKey} classNames="flash-activity" timeout={3000} exit={false}>
                  {reportActivity.item}
                </CSSTransition>
              ))}
            </TransitionGroup>
          )
        }
      </ul>
    </div>
  );
});
